import { formatUsdAmount } from "./finance";

/**
 * How a feasibility quote is denominated. Mirrors `StudyPayRateFrequency`,
 * hand-written here because a "use client" file cannot import `@repo/db`; the
 * feasibility service asserts the two unions stay identical.
 */
export const QUOTE_RATE_FREQUENCIES = [
  "ONE_TIME",
  "HOURLY",
  "PER_TASK",
] as const;

export type QuoteRateFrequency = (typeof QUOTE_RATE_FREQUENCIES)[number];

export interface QuoteRate {
  /** The figure alone, e.g. "$135.00". */
  amount: string;
  /** The unit as it trails the figure: "CPI", "/ hr", "/ task". */
  unit: string;
  /** Figure + unit, e.g. "$135.00 / hr". What a cell or a chip renders. */
  short: string;
  /**
   * The client-facing form, always rendered next to `label`: "$135.00 / hr
   * all-in", but "$125.00 all-in" for a CPI, whose unit the label already says.
   */
  allIn: string;
  /** The unit as a noun, for a label rather than a suffix: "Hourly rate". */
  label: string;
  /**
   * The same thing in the words a researcher uses. "CPI" is our jargon and has
   * no business on a client-facing surface, where the figure has always been
   * introduced as "Per participant".
   */
  clientLabel: string;
  /**
   * The same unit as it reads in a sentence or a hero, where a bare preposition
   * sounds clipped: "an hour" rather than "per hour". Matches the pay copy
   * settled in ENG-1251 ("It pays $60 an hour", "It pays $30 per task").
   */
  sentenceLabel: string;
  /**
   * True only when one figure times the participant count is the whole cost.
   * An hourly or per-task rate multiplied by headcount is not a total, it is
   * the price of one hour (or one task) for everyone, which is a far more
   * misleading number to show than none at all.
   */
  totalScalesWithParticipants: boolean;
}

const UNITS: Record<
  QuoteRateFrequency,
  {
    unit: string;
    label: string;
    clientLabel: string;
    sentenceLabel: string;
    repeatsLabel: boolean;
    totalScalesWithParticipants: boolean;
  }
> = {
  // `repeatsLabel` is the difference between a suffix and a name. "/ hr" trails
  // the label "Hourly rate" fine; "CPI" after the label "CPI" reads as
  // "CPI: $125.00 CPI". So the labelled form drops the unit only there.
  ONE_TIME: {
    unit: "CPI",
    label: "CPI",
    clientLabel: "Per participant",
    sentenceLabel: "per participant",
    repeatsLabel: true,
    totalScalesWithParticipants: true,
  },
  HOURLY: {
    unit: "/ hr",
    label: "Hourly rate",
    clientLabel: "Per hour",
    sentenceLabel: "an hour",
    repeatsLabel: false,
    totalScalesWithParticipants: false,
  },
  PER_TASK: {
    unit: "/ task",
    label: "Per-task rate",
    clientLabel: "Per task",
    sentenceLabel: "per task",
    repeatsLabel: false,
    totalScalesWithParticipants: false,
  },
};

/**
 * The one place a quoted rate is turned into words, so the internal tile, the
 * inbox table, the Slack card and the client reply cannot disagree about what a
 * figure is per.
 *
 * A null frequency renders as CPI. That is not a guess: per-complete is what
 * every quote meant before the pay model existed as a field, and a reply has to
 * name some unit. The place an unstated model has to be visible is the internal
 * inbox, before an operator prices it, not the email afterwards.
 *
 * Returns null when there is no positive amount, so a caller can omit the
 * element rather than print "$0.00 / hr".
 */
export function formatQuoteRate(
  amount: string | number | null | undefined,
  payRateFrequency: QuoteRateFrequency | null | undefined,
): QuoteRate | null {
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }
  if (value <= 0) {
    return null;
  }
  const {
    unit,
    label,
    clientLabel,
    sentenceLabel,
    repeatsLabel,
    totalScalesWithParticipants,
  } = UNITS[payRateFrequency ?? "ONE_TIME"];
  const figure = formatUsdAmount(value);
  const short = `${figure} ${unit}`;
  const allIn = repeatsLabel ? `${figure} all-in` : `${short} all-in`;
  return {
    amount: figure,
    unit,
    short,
    allIn,
    label,
    clientLabel,
    sentenceLabel,
    totalScalesWithParticipants,
  };
}
